import type { ExportRenderConfig, FrameDescriptor, SceneFrameAdapter } from '@/rendering/export/types'
import { getViewportExportApi } from '@/rendering/viewportExportBridge'

/**
 * 将离线渲染管线连接到当前页内的 `ImockupThreeViewport`（经 `viewportExportBridge`）。
 */
export class ViewportSceneAdapter implements SceneFrameAdapter {
  private watermarkCanvas: HTMLCanvasElement | null = null
  private watermarkCtx: CanvasRenderingContext2D | null = null
  private includeWatermark = false

  /**
   * @inheritdoc
   */
  async beginSession(config: ExportRenderConfig): Promise<void> {
    const api = getViewportExportApi()
    if (!api) {
      throw new Error('Viewport export API is not mounted')
    }
    api.beginExportVisualSession({
      suppressEditorOverlay: !config.includeEditorOverlay,
      quality: config.quality,
    })
    api.beginSceneCanvasSize(config.width, config.height)

    this.includeWatermark = config.includeWatermark === true
    if (this.includeWatermark) {
      const canvas = document.createElement('canvas')
      canvas.width = config.width
      canvas.height = config.height
      this.watermarkCanvas = canvas
      this.watermarkCtx = canvas.getContext('2d')
    }
  }

  /**
   * @inheritdoc
   */
  async applyFrame(desc: FrameDescriptor): Promise<void> {
    const api = getViewportExportApi()
    if (!api) {
      throw new Error('Viewport export API is not mounted')
    }
    await Promise.resolve(api.renderFrameAtTime(desc.timeSeconds))

    if (this.includeWatermark && this.watermarkCanvas && this.watermarkCtx) {
      const source = api.getCanvas()
      if (!source) return
      const ctx = this.watermarkCtx
      ctx.clearRect(0, 0, this.watermarkCanvas.width, this.watermarkCanvas.height)
      ctx.drawImage(source, 0, 0)
      drawAnimockWatermark(ctx, this.watermarkCanvas.width, this.watermarkCanvas.height)
    }
  }

  /**
   * @inheritdoc
   */
  getCanvas(): HTMLCanvasElement | null {
    if (this.includeWatermark && this.watermarkCanvas) return this.watermarkCanvas
    return getViewportExportApi()?.getCanvas() ?? null
  }

  /**
   * @inheritdoc
   */
  async endSession(): Promise<void> {
    const api = getViewportExportApi()
    if (!api) return
    api.endSceneCanvasSize()
    api.endExportVisualSession()
    this.watermarkCanvas = null
    this.watermarkCtx = null
    this.includeWatermark = false
  }
}

/**
 * 绘制导出水印（免费版限制）。
 *
 * @param ctx - 2D 上下文
 * @param w - 画布宽
 * @param h - 画布高
 */
function drawAnimockWatermark(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const text = 'Animock'
  const base = Math.max(12, Math.round(Math.min(w, h) * 0.03))
  ctx.save()
  ctx.globalAlpha = 0.18
  ctx.fillStyle = '#0f172a'
  ctx.font = `700 ${base}px system-ui, -apple-system, Segoe UI, Roboto, Arial`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const step = base * 6
  ctx.translate(w / 2, h / 2)
  ctx.rotate(-Math.PI / 8)
  for (let y = -h; y <= h; y += step) {
    for (let x = -w; x <= w; x += step) {
      ctx.fillText(text, x, y)
    }
  }
  ctx.restore()
}
